var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FocusOutlinePositionWatcherEnum } from '@inter-ikea-kompis/component-focus-outline';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Image } from '@inter-ikea-kompis/component-image';
import { Text } from '@inter-ikea-kompis/component-text';
import PillAlignEnum from '../../enums/PillAlignEnum.js';
import PillSizeOptionsEnum from '../../enums/PillSizeOptionsEnum.js';
import PillStyle from './PillStyle.js';
/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Pill class.
 *
 * @event select Pill triggers props select event.
 */
export default class Pill extends KompisElement {
    constructor() {
        super(...arguments);
        // Label associated with pill.
        this.label = null;
        // Align the icon 'right' or 'left'.
        this.alignIcon = PillAlignEnum.left;
        // Select
        this.selected = false;
        // Thumbnail
        this.thumbnail = null;
        // Icon: Icon on pill.
        this.icon = null;
        // Disabled mode
        this.disabled = false;
        // Pill size option
        this.pillSize = PillSizeOptionsEnum.medium;
        // Sets the behaviour of the position watcher. The position watcher is useful when the component is inside an overflown element where it can become cropped. When enabled it will be set itself to be position fixed and update the position of itself live. However, updating the position live can be slow and result in lag on slower computers. Be wary of using 'auto' or 'enabled' if the component have any ancestors with transform styling.
        this.focusOutlinePositionWatcher = FocusOutlinePositionWatcherEnum.auto;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const isLeftIcon = this.alignIcon === 'left';
        const labelClass = `pillButton${this.selected ? ' pillButtonSelected' : ''}`;
        return html `
			<style>
				${PillStyle.getStyle({
            alignIcon: this.alignIcon,
            pillSize: this.pillSize,
            selected: this.selected,
            isDisabled: this.disabled,
            label: this.label,
            thumbnail: this.thumbnail,
            icon: this.icon,
            theme: this.theme
        })}
			</style>
			<kompis-focus-outline
				.theme="${this.theme}"
				.positionWatcher="${this.focusOutlinePositionWatcher}"
				class="pill"
				@keydown="${(event) => this.onKeyDown(event)}"
				@click="${this.onClick.bind(this)}"
				tabindex="${this.disabled ? -1 : ''}"
			>
				<div
					class="${labelClass}"
					tabindex="0"
					role="checkbox"
					aria-label="${this.label || ''}"
					aria-checked="${this.selected}"
					aria-disabled="${this.disabled}"
				>
					${!this.thumbnail ? this.getIcon(isLeftIcon ? this.icon : null, isLeftIcon) : ''}
					${this.getThumbnail(this.thumbnail)} ${this.getText(this.label)}
					${!this.thumbnail ? this.getIcon(!isLeftIcon ? this.icon : null, isLeftIcon) : ''}
				</div>
			</kompis-focus-outline>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-image', Image);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-focus-outline', FocusOutline);
        super.connectedCallback();
    }
    /**
     * Get text.
     *
     * @param text The text to display.
     * @returns Template.
     */
    getText(text) {
        if (!text) {
            return null;
        }
        return html `
			<div class="pillText">
				<kompis-text
					.theme="${this.theme}"
					.type="${this.pillSize === 'small'
            ? ThemeFontStyleTypeEnum.bodySmall
            : ThemeFontStyleTypeEnum.bodyMedium}"
					>${text}</kompis-text
				>
			</div>
		`;
    }
    /**
     * Get the icon.
     *
     * @param icon The icon.
     * @param isLeftIcon Where to place the spacer.
     * @returns Template.
     */
    getIcon(icon, isLeftIcon) {
        if (!icon) {
            return null;
        }
        return html `
			<div class="pillIcon">
				${this.getSpacer(!isLeftIcon)}
				<kompis-icon .theme="${this.theme}" .icon="${icon}"></kompis-icon>
				${this.getSpacer(isLeftIcon)}
			</div>
		`;
    }
    /**
     * Get thumbnail.
     *
     * @param thumbnail The thumbnail.
     * @returns Template.
     */
    getThumbnail(thumbnail) {
        if (!thumbnail) {
            return null;
        }
        return html `
			<div class="thumbnail">
				<kompis-image
					class="thumbnailImage"
					alt="thumbnail image"
					.src="${thumbnail}"
				></kompis-image>
				<div class="divider"></div>
			</div>
		`;
    }
    /**
     * @param show If space should be shown.
     * @returns Template.
     */
    getSpacer(show) {
        if (!show) {
            return null;
        }
        return html ` <div class="pillSpacer"></div> `;
    }
    /**
     * Event handler.
     *
     * @param event HTMLEvent.
     */
    onClick(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.disabled) {
            return;
        }
        this.dispatchEvent(new CustomEvent('select', {
            bubbles: true,
            detail: {
                selected: !this.selected
            }
        }));
    }
    /**
     * Event handler.
     *
     * @param event Event.
     */
    onKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            if (this.disabled) {
                return;
            }
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: {
                    selected: !this.selected
                }
            }));
        }
    }
}
__decorate([
    property({ type: String })
], Pill.prototype, "label", void 0);
__decorate([
    property({ type: String })
], Pill.prototype, "alignIcon", void 0);
__decorate([
    property({ type: Boolean })
], Pill.prototype, "selected", void 0);
__decorate([
    property({ type: String })
], Pill.prototype, "thumbnail", void 0);
__decorate([
    property({ type: String })
], Pill.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], Pill.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], Pill.prototype, "pillSize", void 0);
__decorate([
    property({ type: Object, required: true })
], Pill.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], Pill.prototype, "focusOutlinePositionWatcher", void 0);
//# sourceMappingURL=Pill.js.map